import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { UserGroupDTO } from '../../../dto/user-group/user-group.dto';
import { DunaUserGroupsCreateDialogComponent } from './duna-user-groups-create-dialog.component';
import { DunaUserGroupsEditDialogComponent } from './duna-user-groups-edit-dialog.component';
import {
  DunaUserGroupsMembersDialogComponent,
  DUNA_USER_GROUPS_MEMBERS_DIALOG_CONFIG,
} from './duna-user-groups-members-dialog.component';
import {
  DunaUserGroupsViewMembersDialogComponent,
  DUNA_USER_GROUPS_VIEW_MEMBERS_DIALOG_CONFIG,
} from './duna-user-groups-view-members-dialog.component';
import { environment } from '../../../environment/environment';

@Component({
  selector: 'app-duna-user-groups',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-user-groups.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-user-groups.component.scss',
})
export class DunaUserGroupsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private viewListener = (e: Event) => this.handleViewMembers(e as CustomEvent);
  private manageListener = (e: Event) =>
    this.handleManageMembers(e as CustomEvent);
  private editListener = (e: Event) => this.handleEdit(e as CustomEvent);
  private deleteListener = (e: Event) => this.handleDelete(e as CustomEvent);

  @ViewChild('groupsTable') groupsTable?: DynamicTableComponent;

  readonly dynamicInput = {
    dataSource: { type: 'text', value: 'duna_user_groups' },
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private componentDataSourceService: ComponentDataSourceService
  ) {}

  ngOnInit(): void {
    window.addEventListener(
      'duna-user-groups-view-members',
      this.viewListener as EventListener
    );
    window.addEventListener(
      'duna-user-groups-manage-members',
      this.manageListener as EventListener
    );
    window.addEventListener(
      'duna-user-groups-edit',
      this.editListener as EventListener
    );
    window.addEventListener(
      'duna-user-groups-delete',
      this.deleteListener as EventListener
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'duna-user-groups-view-members',
      this.viewListener as EventListener
    );
    window.removeEventListener(
      'duna-user-groups-manage-members',
      this.manageListener as EventListener
    );
    window.removeEventListener(
      'duna-user-groups-edit',
      this.editListener as EventListener
    );
    window.removeEventListener(
      'duna-user-groups-delete',
      this.deleteListener as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  openCreateDialog(): void {
    this.dialog
      .open(DunaUserGroupsCreateDialogComponent, { width: '480px' })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) this.refresh();
      });
  }

  private handleViewMembers(event: CustomEvent): void {
    this.dialog.open(DunaUserGroupsViewMembersDialogComponent, {
      ...DUNA_USER_GROUPS_VIEW_MEMBERS_DIALOG_CONFIG,
      data: { group: event.detail as UserGroupDTO },
    });
  }

  private handleManageMembers(event: CustomEvent): void {
    this.dialog
      .open(DunaUserGroupsMembersDialogComponent, {
        ...DUNA_USER_GROUPS_MEMBERS_DIALOG_CONFIG,
        data: { group: event.detail as UserGroupDTO },
      })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) this.refresh();
      });
  }

  private handleEdit(event: CustomEvent): void {
    this.dialog
      .open(DunaUserGroupsEditDialogComponent, {
        width: '480px',
        data: { group: event.detail as UserGroupDTO },
      })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) this.refresh();
      });
  }

  private handleDelete(event: CustomEvent): void {
    const group = event.detail as UserGroupDTO;
    this.dialog
      .open(ConfirmResetDialogComponent, {
        width: '420px',
        data: {
          title: 'Slet gruppering',
          message: `Er du sikker på, at du vil slette grupperingen "${group.name}"? Tildelinger fjernes.`,
          confirmText: 'Slet',
          cancelText: 'Annuller',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.http
          .delete(`${environment.apiUrl}/user-groups/${group.group_id}`)
          .subscribe({
            next: () => this.refresh(),
          });
      });
  }

  private refresh(): void {
    this.groupsTable?.refreshTable();
    this.componentDataSourceService.refreshData();
    this.cdr.detectChanges();
  }
}
